import { useCallback, useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { P, fmtCpf } from "./util";

const EST = { valido: "ok", invalido: "falhou", nao_verificavel: "não verificável", nao_se_aplica: "não se aplica" };

export function Relogio({ podeEditar }) {
  const [reps, setReps] = useState([]);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ultimo, setUltimo] = useState(null);
  const [liberado, setLiberado] = useState(null);
  const load = useCallback(() => {
    P("/reps").then(setReps).catch(() => {}); P("/importacoes").then(setHist).catch(() => {});
    P("/recursos").then((r) => setLiberado(!!r?.afd?.liberado)).catch(() => setLiberado(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const enviar = async (arquivo, aceitar = false) => {
    if (!arquivo) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    try {
      const { data } = await api.post(`/ponto/afd/importar${aceitar ? "?aceitar_outro_cnpj=true" : ""}`, fd);
      setUltimo(data);
      toast.success(`${data.novas} marcação(ões) nova(s) importada(s)${data.repetidas ? `, ${data.repetidas} já estavam no sistema` : ""}.`);
      load();
    } catch (e) {
      if (e.response?.status === 409 && !aceitar && window.confirm(`${errMsg(e)}\n\nImportar mesmo assim?`)) return enviar(arquivo, true);
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const renomear = (r, nome) => api.put(`/ponto/reps/${r.id}`, { nome, local: r.local || "" }).then(load).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card-surface space-y-3 p-4">
        <p className="font-semibold">Importar o arquivo do relógio (AFD)</p>
        <ol className="ml-4 list-decimal text-sm text-muted-foreground">
          <li>No relógio de ponto, coloque o pendrive e escolha exportar o <b>AFD</b> (arquivo fonte de dados).</li>
          <li>No computador, clique abaixo e escolha o arquivo (geralmente "AFD…txt").</li>
          <li>Pode importar o mesmo arquivo de novo: as marcações repetidas são ignoradas.</li>
        </ol>
        {liberado === false && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm" data-testid="ponto-afd-homologacao">
            <b>Em homologação.</b> A importação do arquivo do relógio físico ainda está sendo testada com equipamentos reais e será liberada
            assim que concluída. Enquanto isso, as marcações podem ser incluídas manualmente no espelho, com motivo.
          </p>
        )}
        {liberado !== true ? null : podeEditar ? (
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? "Importando…" : "Escolher arquivo AFD"}
            <input type="file" accept=".txt,.afd,text/plain" className="hidden" disabled={busy} onChange={(e) => { enviar(e.target.files?.[0]); e.target.value = ""; }} data-testid="ponto-afd-input" />
          </label>
        ) : <p className="text-sm text-muted-foreground">Somente consulta.</p>}
        <p className="text-xs text-muted-foreground">Aceita relógios REP-C da Portaria 671/2021 e os antigos da Portaria 1.510/2009. O arquivo original nunca é alterado.</p>
        {ultimo && (
          <div className="rounded-lg border p-3 text-sm" data-testid="ponto-afd-resultado">
            <p><b>{ultimo.arquivo}</b> · leiaute {ultimo.leiaute === "671" ? "Portaria 671" : "Portaria 1.510"}</p>
            <p>{ultimo.marcacoes} marcações no arquivo · {ultimo.novas} novas · {ultimo.repetidas} repetidas{ultimo.ignoradas ? ` · ${ultimo.ignoradas} linhas ignoradas` : ""}</p>
            {ultimo.integridade?.situacao && <p className={`mt-1 font-medium ${{ integro: "text-emerald-700", parcial: "text-amber-600", com_falhas: "text-red-600" }[ultimo.integridade.situacao]}`}>
              Conferência do arquivo: {{ integro: "íntegro (tudo conferido)", parcial: "conferência parcial", com_falhas: "COM FALHAS" }[ultimo.integridade.situacao]}
              <span className="block text-xs font-normal">
                Sequência (NSR): {EST[ultimo.integridade.sequencia]} · CRC: {EST[ultimo.integridade.crc?.estado]}{ultimo.integridade.crc?.total ? ` (${ultimo.integridade.crc.conferidas}/${ultimo.integridade.crc.total} linhas)` : ""}
                {ultimo.integridade.hash?.estado !== "nao_se_aplica" ? ` · Hash REP-P: ${EST[ultimo.integridade.hash?.estado]}` : ""} · Totais do fim do arquivo: {EST[ultimo.integridade.trailer]}
                {ultimo.integridade.situacao === "parcial" ? ". Parte não pôde ser conferida (fabricante com outro cálculo ou leiaute antigo): isso não atesta que o arquivo está íntegro." : ""}
              </span></p>}
            {ultimo.integridade?.alerta ? (
              <p className="mt-1 text-red-600">Atenção na conferência do arquivo:
                {ultimo.integridade.nsr_faltando_total ? ` ${ultimo.integridade.nsr_faltando_total} registro(s) faltando na sequência (NSR);` : ""}
                {ultimo.integridade.nsr_repetido?.length ? ` NSR repetido;` : ""}
                {ultimo.integridade.crc_invalido?.length ? ` ${ultimo.integridade.crc_invalido.length} linha(s) com código de conferência (CRC) inválido;` : ""}
                {ultimo.integridade.hash_invalido?.length ? ` ${ultimo.integridade.hash_invalido.length} marcação(ões) com hash inválido;` : ""}
                {ultimo.integridade.trailer_ok === false ? " totais do fim do arquivo não batem;" : ""}
                {" "}o arquivo pode estar incompleto ou ter sido alterado. Exporte de novo direto do relógio.</p>
            ) : null}
            {ultimo.em_mes_fechado > 0 && <p className="mt-1 text-amber-600">{ultimo.em_mes_fechado} marcação(ões) do arquivo são de mês já fechado: ficam guardadas, mas o espelho fechado não muda. Para considerar, o administrador reabre o mês.</p>}
            {ultimo.sem_cadastro?.length > 0 && <p className="mt-1 text-amber-600">Sem cadastro no ponto: {ultimo.sem_cadastro.map((s) => `${s.nome || "?"} (${s.id.length === 11 ? fmtCpf(s.id) : s.id})`).join(", ")}</p>}
          </div>
        )}
      </div>
      <div className="card-surface space-y-3 p-4">
        <p className="font-semibold">Relógios cadastrados</p>
        {!reps.length && <p className="text-sm text-muted-foreground">O relógio é cadastrado sozinho na primeira importação.</p>}
        {reps.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm">
            <Input defaultValue={r.nome} disabled={!podeEditar} onBlur={(e) => e.target.value !== r.nome && renomear(r, e.target.value)} className="h-9" />
            <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">nº {r.numero}</span>
          </div>
        ))}
        <p className="pt-2 font-semibold">Importações</p>
        <ul className="max-h-72 space-y-1 overflow-y-auto text-xs">
          {hist.map((h) => <li key={h.id} className="border-b py-1">{fmtDateTime(h.em)} · {h.arquivo} · {h.novas} novas / {h.marcacoes} · por {h.por}</li>)}
        </ul>
      </div>
    </div>
  );
}
